import { clamp, preventDefault, isMobileBreakpoint } from './utilities.js';
import { ZoomDialog } from './zoom-dialog.js';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const DEFAULT_ZOOM = 1.5;
const V2_ZOOM_LEVELS = [1, 2, 3.5];
const V2_MAX_ZOOM = V2_ZOOM_LEVELS[V2_ZOOM_LEVELS.length - 1];
const ZOOM_TOLERANCE = 0.05;
const PAN_EDGE_INSET = 0.1;
const DOUBLE_TAP_DELAY = 300;
const DOUBLE_TAP_DISTANCE = 50;
const DRAG_THRESHOLD = 10;

/** @type {WeakSet<ZoomDialog>} */
const patchedDialogs = new WeakSet();

export class DragZoomWrapper extends HTMLElement {
  #controller = new AbortController();
  /** @type {number} */
  #scale = DEFAULT_ZOOM;
  /** @type {number} */
  #initialDistance = 0;
  /** @type {number} */
  #startScale = DEFAULT_ZOOM;
  /** @type {Point} */
  #translate = { x: 0, y: 0 };
  /** @type {Point} */
  #startPosition = { x: 0, y: 0 };
  /** @type {Point} */
  #startTranslate = { x: 0, y: 0 };
  /** @type {boolean} */
  #isDragging = false;
  /** @type {boolean} */
  #isPinching = false;
  /** @type {boolean} */
  #initialized = false;
  /** @type {boolean} */
  #desktopInitialized = false;
  /** @type {number | null} */
  #animationFrame = null;
  /** @type {number} */
  #lastTapTime = 0;
  /** @type {Point | null} */
  #lastTapPosition = null;

  /** @type {boolean} */
  #hasDraggedBeyondThreshold = false;

  /** @type {boolean} */
  #hasManualZoom = false;

  /** @type {1 | -1} */
  #zoomDirection = 1;

  /** @type {number} */
  #lastEmittedScale = DEFAULT_ZOOM;

  get #image() {
    return this.querySelector('img');
  }

  get #target() {
    return this.querySelector('[data-drag-zoom-target]') || this.#image;
  }

  get #desktopZoom() {
    return this.hasAttribute('data-desktop-zoom');
  }

  get #defaultScale() {
    const attr = this.getAttribute('data-initial-scale');
    if (attr != null && attr !== '') {
      const value = Number(attr);
      return Number.isFinite(value) && value > 0 ? value : MIN_ZOOM;
    }
    return DEFAULT_ZOOM;
  }

  get #maxZoom() {
    return this.#desktopZoom ? V2_MAX_ZOOM : MAX_ZOOM;
  }

  get #isZoomed() {
    return this.#scale > MIN_ZOOM + ZOOM_TOLERANCE;
  }

  get scale() {
    return this.#scale;
  }

  connectedCallback() {
    if (!this.#target) return;

    this.#scale = this.#defaultScale;
    this.#startScale = this.#defaultScale;

    this.#initResizeListener();
    this.#setupDialogCloseListener();
    this.#updateTransform();

    if (this.#desktopZoom) {
      this.#initDesktopListeners();
    }

    if (!isMobileBreakpoint()) return;

    this.#initEventListeners();
    this.#updateTransform();
  }

  #initResizeListener() {
    this.#resizeObserver.observe(this);
  }

  /**
   * Override parent zoom dialog's close method once to reset every wrapper.
   */
  #setupDialogCloseListener() {
    const zoomDialog = /** @type {ZoomDialog} */ (this.closest('zoom-dialog'));
    if (!zoomDialog || typeof zoomDialog.close !== 'function') return;
    if (patchedDialogs.has(zoomDialog)) return;

    patchedDialogs.add(zoomDialog);

    const originalClose = zoomDialog.close.bind(zoomDialog);

    zoomDialog.close = async (...args) => {
      zoomDialog.querySelectorAll('drag-zoom-wrapper').forEach((wrapper) => {
        if (wrapper instanceof DragZoomWrapper) wrapper.reset();
      });
      return await originalClose(...args);
    };
  }

  #initEventListeners() {
    if (this.#initialized) return;
    this.#initialized = true;
    const { signal } = this.#controller;
    const options = { passive: false, signal };

    this.addEventListener('touchstart', this.#handleTouchStart, options);
    this.addEventListener('touchmove', this.#handleTouchMove, options);
    this.addEventListener('touchend', this.#handleTouchEnd, options);

    this.#updateTransform();
  }

  #initDesktopListeners() {
    if (this.#desktopInitialized) return;
    this.#desktopInitialized = true;
    const { signal } = this.#controller;

    this.addEventListener('pointermove', this.#handlePointerMove, { signal });
    this.addEventListener('click', this.#handleClick, { signal });
    this.addEventListener('wheel', this.#handleWheel, { passive: false, signal });
    this.#syncCursor();
  }

  disconnectedCallback() {
    this.#controller.abort();
    this.#resizeObserver.disconnect();
    this.#cancelAnimationFrame();
  }

  #handleResize = () => {
    if (!this.#initialized && isMobileBreakpoint()) {
      this.#initEventListeners();
    }

    if (this.#desktopZoom && !this.#desktopInitialized && !isMobileBreakpoint()) {
      this.#initDesktopListeners();
    }

    if (this.#initialized || this.#desktopInitialized) {
      this.#requestUpdateTransform();
    }
  };

  #resizeObserver = new ResizeObserver(this.#handleResize);

  /**
   * @param {TouchEvent} event
   */
  #handleTouchStart = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      preventDefault(event);
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (!touch1 || !touch2) return;
      this.#startZoomGestureFromTouches(touch1, touch2);
    } else if (touchCount === 1) {
      const touch = event.touches[0];
      if (!touch) return;

      const currentTime = performance.now();
      const timeSinceLastTap = currentTime - this.#lastTapTime;

      if (timeSinceLastTap >= DOUBLE_TAP_DELAY) {
        this.#storeTapInfo(currentTime, touch);
        this.#startDragGestureFromTouch(touch);
        return;
      }

      if (this.#lastTapPosition) {
        const distance = getDistance(touch, this.#lastTapPosition);

        if (distance < DOUBLE_TAP_DISTANCE) {
          preventDefault(event);
          this.#handleDoubleTap(touch);
          this.#lastTapTime = 0;
          this.#lastTapPosition = null;
          return;
        }
      }

      this.#storeTapInfo(currentTime, touch);
      this.#startDragGestureFromTouch(touch);
    }
  };

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #startZoomGestureFromTouches(touch1, touch2) {
    this.#initialDistance = getDistance(touch1, touch2);
    this.#startScale = this.#scale;
    this.#isDragging = false;
    this.#isPinching = true;
  }

  /**
   * @param {Touch} touch
   */
  #startDragGestureFromTouch(touch) {
    this.#startPosition = { x: touch.clientX, y: touch.clientY };
    this.#startTranslate = { x: this.#translate.x, y: this.#translate.y };
    this.#isDragging = true;
    this.#hasDraggedBeyondThreshold = false;
  }

  /**
   * @param {number} currentTime
   * @param {Touch} touch
   */
  #storeTapInfo(currentTime, touch) {
    this.#lastTapTime = currentTime;
    this.#lastTapPosition = { x: touch.clientX, y: touch.clientY };
  }

  /**
   * @param {Touch} point
   */
  #handleDoubleTap(point) {
    if (this.#desktopZoom) {
      this.#zoomToAnchor(this.#nextCycleScale(), point.clientX, point.clientY);
      return;
    }

    let targetZoom;

    if (this.#hasManualZoom) {
      targetZoom = MIN_ZOOM;
      this.#hasManualZoom = false;
    } else {
      targetZoom = Math.abs(this.#scale - MIN_ZOOM) < ZOOM_TOLERANCE ? DEFAULT_ZOOM : MIN_ZOOM;
    }

    this.#zoomToAnchor(targetZoom, point.clientX, point.clientY);
  }

  /**
   * Zooms so the content under the given viewport point stays put.
   * @param {number} targetScale
   * @param {number} clientX
   * @param {number} clientY
   */
  #zoomToAnchor(targetScale, clientX, clientY) {
    const oldScale = this.#scale;
    this.#scale = clamp(targetScale, MIN_ZOOM, this.#maxZoom);

    if (!this.#isZoomed) {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
      this.#hasManualZoom = false;
    } else {
      const offset = this.#offsetFromCenter(clientX, clientY);
      this.#translate.x += offset.x / this.#scale - offset.x / oldScale;
      this.#translate.y += offset.y / this.#scale - offset.y / oldScale;
    }

    this.#syncCursor();
    this.#requestUpdateTransform();
  }

  /**
   * @param {number} clientX
   * @param {number} clientY
   * @returns {Point}
   */
  #offsetFromCenter(clientX, clientY) {
    const rect = this.getBoundingClientRect();
    return {
      x: clientX - (rect.left + rect.width / 2),
      y: clientY - (rect.top + rect.height / 2),
    };
  }

  /**
   * Next stop in the 1x -> 2x -> 3.5x -> 2x -> 1x cycle. Reverses direction at either end.
   * @returns {number}
   */
  #nextCycleScale() {
    const direction = this.#cycleDirection();
    this.#zoomDirection = direction;
    const scale = this.#scale;

    const next =
      direction > 0
        ? V2_ZOOM_LEVELS.find((level) => level > scale + ZOOM_TOLERANCE)
        : [...V2_ZOOM_LEVELS].reverse().find((level) => level < scale - ZOOM_TOLERANCE);

    return next ?? MIN_ZOOM;
  }

  /**
   * @returns {1 | -1}
   */
  #cycleDirection() {
    if (this.#scale >= V2_MAX_ZOOM - ZOOM_TOLERANCE) return -1;
    if (!this.#isZoomed) return 1;
    return this.#zoomDirection;
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchMove = (event) => {
    const touchCount = event.touches.length;

    if (touchCount === 2) {
      preventDefault(event);
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      if (touch1 && touch2) {
        this.#processZoomGesture(touch1, touch2);
      }
    } else if (touchCount === 1 && this.#isDragging) {
      const touch = event.touches[0];
      if (!touch) return;

      if (!this.#isZoomed) {
        const dx = touch.clientX - this.#startPosition.x;
        const dy = touch.clientY - this.#startPosition.y;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 8) {
          this.#isDragging = false;
        }
        return;
      }

      preventDefault(event);
      this.#processDragGesture(touch);
    }
  };

  /**
   * @param {Touch} touch1
   * @param {Touch} touch2
   */
  #processZoomGesture(touch1, touch2) {
    const midX = (touch1.clientX + touch2.clientX) / 2;
    const midY = (touch1.clientY + touch2.clientY) / 2;
    const currentDistance = getDistance(touch1, touch2);
    const oldScale = this.#scale;
    const newScale = (currentDistance / this.#initialDistance) * this.#startScale;
    this.#scale = clamp(newScale, MIN_ZOOM, this.#maxZoom);
    this.#hasManualZoom = true;

    const offset = this.#offsetFromCenter(midX, midY);
    this.#translate.x += offset.x / this.#scale - offset.x / oldScale;
    this.#translate.y += offset.y / this.#scale - offset.y / oldScale;

    this.#requestUpdateTransform();
    this.#isDragging = false;
  }

  /**
   * @param {Touch} point
   */
  #processDragGesture(point) {
    const distance = getDistance(point, this.#startPosition);

    if (!this.#hasDraggedBeyondThreshold && distance < DRAG_THRESHOLD) {
      return;
    }

    this.#hasDraggedBeyondThreshold = true;

    const dx = point.clientX - this.#startPosition.x;
    const dy = point.clientY - this.#startPosition.y;

    this.#translate.x = this.#startTranslate.x + dx / this.#scale;
    this.#translate.y = this.#startTranslate.y + dy / this.#scale;

    this.#requestUpdateTransform();
  }

  /**
   * @param {TouchEvent} event
   */
  #handleTouchEnd = (event) => {
    if (event.touches.length === 0) {
      this.#isDragging = false;
      this.#isPinching = false;
      this.#requestUpdateTransform();
      this.#hasDraggedBeyondThreshold = false;
    }
  };

  /**
   * Desktop: while zoomed, the cursor position inside the frame picks which part of the image is shown.
   * @param {PointerEvent} event
   */
  #handlePointerMove = (event) => {
    if (event.pointerType === 'touch' || isMobileBreakpoint()) return;
    if (!this.#isZoomed) return;
    this.#panToPointer(event.clientX, event.clientY);
    this.#requestUpdateTransform();
  };

  /**
   * @param {number} clientX
   * @param {number} clientY
   */
  #panToPointer(clientX, clientY) {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const insetX = rect.width * PAN_EDGE_INSET;
    const insetY = rect.height * PAN_EDGE_INSET;
    const px = clamp((clientX - rect.left - insetX) / (rect.width - insetX * 2), 0, 1);
    const py = clamp((clientY - rect.top - insetY) / (rect.height - insetY * 2), 0, 1);
    const { x: maxX, y: maxY } = this.#maxTranslate();

    this.#translate.x = (0.5 - px) * 2 * maxX;
    this.#translate.y = (0.5 - py) * 2 * maxY;
  }

  /**
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    if (isMobileBreakpoint()) return;
    event.preventDefault();
    event.stopPropagation();

    this.#scale = clamp(this.#nextCycleScale(), MIN_ZOOM, this.#maxZoom);
    this.#hasManualZoom = false;

    if (this.#isZoomed) {
      this.#panToPointer(event.clientX, event.clientY);
    } else {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
    }

    this.#syncCursor();
    this.#requestUpdateTransform();
  };

  /**
   * @param {WheelEvent} event
   */
  #handleWheel = (event) => {
    if (isMobileBreakpoint()) return;
    if (!this.closest('zoom-dialog dialog[open]')) return;

    event.preventDefault();

    const factor = event.ctrlKey ? 0.02 : 0.0025;
    this.#scale = clamp(this.#scale * (1 - event.deltaY * factor), MIN_ZOOM, this.#maxZoom);
    this.#hasManualZoom = this.#isZoomed;

    if (this.#isZoomed) {
      this.#panToPointer(event.clientX, event.clientY);
    } else {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
    }

    this.#syncCursor();
    this.#requestUpdateTransform();
  };

  zoomIn() {
    this.#stepZoom(1);
  }

  zoomOut() {
    this.#stepZoom(-1);
  }

  reset() {
    this.#resetZoom();
  }

  /**
   * @param {1 | -1} direction
   */
  #stepZoom(direction) {
    const scale = this.#scale;
    const next =
      direction > 0
        ? V2_ZOOM_LEVELS.find((level) => level > scale + ZOOM_TOLERANCE) ?? V2_MAX_ZOOM
        : [...V2_ZOOM_LEVELS].reverse().find((level) => level < scale - ZOOM_TOLERANCE) ?? MIN_ZOOM;

    this.#zoomDirection = direction;
    this.#scale = next;
    this.#hasManualZoom = this.#isZoomed;
    if (!this.#isZoomed) {
      this.#scale = MIN_ZOOM;
      this.#translate = { x: 0, y: 0 };
    }
    this.#syncCursor();
    this.#requestUpdateTransform();
  }

  #syncCursor() {
    if (!this.#desktopZoom || isMobileBreakpoint()) return;
    this.style.cursor = this.#cycleDirection() > 0 ? 'zoom-in' : 'zoom-out';
  }

  /**
   * Largest translation (in unscaled px) that keeps the scaled content covering the frame.
   * @returns {Point}
   */
  #maxTranslate() {
    const wrapperRect = this.getBoundingClientRect();
    const target = this.#target;
    const hasStage = target instanceof HTMLElement && target !== this.#image;
    let actualImageWidth = hasStage ? target.offsetWidth || wrapperRect.width : wrapperRect.width;
    let actualImageHeight = hasStage ? target.offsetHeight || wrapperRect.height : wrapperRect.height;

    const snapshot = hasStage ? target.querySelector('img.lightbox-snapshot:not([hidden])') : null;
    const containedImage = hasStage ? target.querySelector('img.product-media__image') : this.#image;

    if (snapshot instanceof HTMLImageElement && snapshot.offsetWidth > 0) {
      actualImageWidth = snapshot.offsetWidth;
      actualImageHeight = snapshot.offsetHeight;
    } else if (
      containedImage instanceof HTMLImageElement &&
      containedImage.naturalWidth > 0 &&
      containedImage.naturalHeight > 0
    ) {
      // object-fit: contain leaves letterbox space; only the drawn image should be pannable.
      const imageAspectRatio = containedImage.naturalWidth / containedImage.naturalHeight;
      const boxAspectRatio = actualImageWidth / actualImageHeight;

      if (imageAspectRatio > boxAspectRatio) {
        actualImageHeight = actualImageWidth / imageAspectRatio;
      } else {
        actualImageWidth = actualImageHeight * imageAspectRatio;
      }
    }

    const horizontalOverflow = Math.max(0, actualImageWidth * this.#scale - wrapperRect.width);
    const verticalOverflow = Math.max(0, actualImageHeight * this.#scale - wrapperRect.height);
    return {
      x: horizontalOverflow / 2 / this.#scale,
      y: verticalOverflow / 2 / this.#scale,
    };
  }

  #constrainTranslation() {
    if (!this.clientWidth || !this.clientHeight) return;

    this.#scale = clamp(this.#scale, MIN_ZOOM, this.#maxZoom);

    if (this.#scale <= MIN_ZOOM) {
      this.#translate.x = 0;
      this.#translate.y = 0;
      this.#applyTransformVars();
      return;
    }

    const { x: maxTranslateX, y: maxTranslateY } = this.#maxTranslate();
    this.#translate.x = clamp(this.#translate.x, -maxTranslateX, maxTranslateX);
    this.#translate.y = clamp(this.#translate.y, -maxTranslateY, maxTranslateY);

    this.#applyTransformVars();
  }

  #applyTransformVars() {
    this.style.setProperty('--drag-zoom-scale', this.#scale.toString());
    this.style.setProperty('--drag-zoom-translate-x', `${this.#translate.x}px`);
    this.style.setProperty('--drag-zoom-translate-y', `${this.#translate.y}px`);
    this.classList.toggle('is-zoomed', this.#isZoomed);
    this.classList.toggle('is-gesturing', this.#isPinching || (this.#isDragging && this.#hasDraggedBeyondThreshold));
    if (Math.abs(this.#scale - this.#lastEmittedScale) >= ZOOM_TOLERANCE) {
      this.#lastEmittedScale = this.#scale;
      this.dispatchEvent(
        new CustomEvent('zoomchange', {
          bubbles: true,
          detail: { scale: this.#scale },
        })
      );
    }
  }

  #requestUpdateTransform = () => {
    if (!this.#animationFrame) {
      this.#animationFrame = requestAnimationFrame(this.#updateTransform);
    }
  };

  #cancelAnimationFrame() {
    if (this.#animationFrame) {
      cancelAnimationFrame(this.#animationFrame);
      this.#animationFrame = null;
    }
  }

  #updateTransform = () => {
    this.#animationFrame = null;
    this.#constrainTranslation();
  };

  #resetZoom() {
    this.#scale = this.#defaultScale;
    this.#startScale = this.#defaultScale;
    this.#translate.x = 0;
    this.#translate.y = 0;
    this.#startPosition = { x: 0, y: 0 };
    this.#startTranslate = { x: 0, y: 0 };
    this.#isDragging = false;
    this.#isPinching = false;
    this.#lastTapTime = 0;
    this.#lastTapPosition = null;
    this.#hasDraggedBeyondThreshold = false;
    this.#hasManualZoom = false;
    this.#zoomDirection = 1;
    this.#lastEmittedScale = this.#scale;
    this.#applyTransformVars();
    this.#syncCursor();
  }

  destroy() {
    this.#controller.abort();
    this.#cancelAnimationFrame();
  }
}

/**
 * @param {Point | Touch | PointerEvent | MouseEvent} point1
 * @param {Point | Touch | PointerEvent | MouseEvent} point2
 * @returns {number}
 */
function getDistance(point1, point2) {
  const x1 = /** @type {Point} */ (point1).x ?? /** @type {Touch} */ (point1).clientX;
  const y1 = /** @type {Point} */ (point1).y ?? /** @type {Touch} */ (point1).clientY;
  const x2 = /** @type {Point} */ (point2).x ?? /** @type {Touch} */ (point2).clientX;
  const y2 = /** @type {Point} */ (point2).y ?? /** @type {Touch} */ (point2).clientY;

  const dx = x1 - x2;
  const dy = y1 - y2;
  return Math.sqrt(dx * dx + dy * dy);
}

if (!customElements.get('drag-zoom-wrapper')) {
  customElements.define('drag-zoom-wrapper', DragZoomWrapper);
}

/**
 * @typedef {Object} Point
 * @property {number} x
 * @property {number} y
 */

/**
 * @typedef {HTMLElement} ZoomDialogElement
 * @property {Function} close - Method to close the zoom dialog
 */
